import { SHAPE } from "./capacity";

function str(value) {
  return value == null ? "" : String(value).trim();
}

function numId(value) {
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

export function buildCustomerPayload(state) {
  const form = state.customerForm;
  const attrs = {
    name: str(form.name),
    company_id: numId(form.company_id),
    entity_type_id: numId(form.entity_type_id),
    status_type_id: numId(form.status_type_id) || 1,
  };
  if (form.entity_type_id && String(form.entity_type_id) !== "2") {
    attrs.first_name = str(form.first_name);
    attrs.last_name = str(form.last_name);
  }
  return {
    data: {
      type: "entities",
      attributes: {
        entity: attrs,
        addresses: [],
        contacts: [],
        // Item creation is deferred: the backend item loop currently crashes
        // when items are present (temporal-dead-zone in modules/index.js).
        // The asset is created separately after the customer transaction.
        items: [],
        serviceItems: [],
      },
    },
  };
}

export function buildAddressPayload(form, entityId) {
  return {
    address_type_id: numId(form.address_type_id) || 1,
    entity_id: numId(entityId),
    name: str(form.name),
    street_address1: str(form.street_address1),
    street_address2: str(form.street_address2),
    city: str(form.city),
    state: str(form.state),
    postal_code: str(form.postal_code),
    country: str(form.country) || "US",
    latitude: form.latitude === "" ? null : Number(form.latitude),
    longitude: form.longitude === "" ? null : Number(form.longitude),
    address_main_type: "entity",
    status_type_id: numId(form.status_type_id) || 1,
  };
}

export function buildAssetPayload(form, entityId, addressId) {
  const attributes = {};
  Object.entries(form.attributes || {}).forEach(([id, value]) => {
    const v = str(value);
    if (v) attributes[String(id)] = v;
  });
  return {
    name: str(form.name),
    entity_id: numId(entityId),
    address_id: numId(addressId),
    item_id: numId(form.item_id),
    status_type_id: numId(form.status_type_id) || 1,
    map_identifier: str(form.map_identifier),
    attributes,
  };
}

export function buildInstallationDraftPayload(state, assetId) {
  return {
    action: "confirm",
    data: {
      entity_id: numId(state.customer?.entity_id),
      asset_id: numId(assetId),
      device_name: str(state.device.device_name),
      identifier: str(state.device.map_identifier),
      installation_date: str(state.device.installation_date),
      attributes: submittedAttributes(state),
      thresholds: submittedThresholds(state),
    },
  };
}

export function submittedAttributes(state) {
  // An explicitly accepted helper geometry is merged over whatever the asset
  // already carries (existing tank) or the form produced (new tank), so
  // unrelated attributes survive. The backend completion schema rejects empty
  // attribute values, so blank overrides are dropped here instead of being
  // sent: stale shape-specific dimensions are simply ignored by the shape's
  // own required-dimension set rather than cleared through this endpoint.
  if (state.tankGeometry) {
    const overrides = tankAttributeMap(state.tankGeometry, null, true);
    const original = state.assetForm?.capacity?.attributeValues
      ? Object.entries(state.assetForm.capacity.attributeValues)
          .map(([attribute_id, value]) => ({ attribute_id, value }))
      : state.branch?.assetMode === "new"
        ? Object.entries(state.assetForm?.attributes || {})
            .map(([attribute_id, value]) => ({ attribute_id, value }))
        : state.asset?.attributes || [];
    const merged = new Map(
      original
        .filter((row) => numId(row.attribute_id) && str(row.value))
        .map((row) => [String(numId(row.attribute_id)), str(row.value)]),
    );
    Object.entries(overrides).forEach(([id, value]) => {
      if (!str(value)) return;
      merged.set(String(numId(id)), str(value));
    });
    return [...merged].map(([id, value]) => ({
      attribute_id: numId(id),
      value,
    })).filter((row) => row.attribute_id);
  }
  if (state.assetForm?.capacity?.attributeValues) {
    return Object.entries(state.assetForm.capacity.attributeValues)
      .filter(([, value]) => str(value))
      .map(([id, value]) => ({ attribute_id: numId(id), value: str(value) }))
      .filter((row) => row.attribute_id);
  }
  if (state.asset?.attributes) {
    return state.asset.attributes
      .filter((row) => str(row.value))
      .map((row) => ({ attribute_id: numId(row.attribute_id), value: str(row.value) }))
      .filter((row) => row.attribute_id);
  }
  return [];
}

export function submittedThresholds(state) {
  return state.thresholds
    .filter((row) => str(row.threshold_value))
    .map((row) => ({
      parameter_id: numId(row.parameter_id),
      threshold_type_id: numId(row.threshold_type_id),
      alert_type_id: numId(row.alert_type_id),
      comparison_operator_id: numId(row.comparison_operator_id),
      threshold_value: str(row.threshold_value),
      alert_enabled: Boolean(row.alert_enabled),
      event_enabled: Boolean(row.event_enabled),
    }))
    .filter((row) => row.parameter_id && row.threshold_type_id && row.alert_type_id && row.comparison_operator_id);
}

export function buildCompletionPayload(state, { entityId, addressId, assetId }) {
  return {
    attest_data_accuracy: true,
    accept_terms: true,
    installation_date: str(state.device.installation_date),
    target_entity_id: numId(entityId),
    target_address_id: numId(addressId),
    entity_address_item_id: numId(assetId),
    device_name: str(state.device.device_name),
    asset: {
      map_identifier: str(state.device.map_identifier),
      attributes: submittedAttributes(state),
    },
    thresholds: submittedThresholds(state),
  };
}

export function buildContactPayload(contact, entityId) {
  return {
    data: {
      attributes: {
        first_name: str(contact.first_name),
        last_name: str(contact.last_name),
        contactType: { contact_type_id: numId(contact.contact_type_id) || 1 },
        statusType: { status_type_id: numId(contact.status_type_id) || 1 },
        phoneAddresses: contact.phone
          ? [{ contact_address: str(contact.phone), description: "Mobile", address_type_id: 1 }]
          : [],
        emailAddresses: contact.email
          ? [{ contact_address: str(contact.email), description: "Primary Email", address_type_id: 2 }]
          : [],
        faxAddresses: [],
        target_entity_id: numId(entityId),
        create_user_account: Boolean(contact.invite),
      },
      selectedPhoneId: 0,
      selectedEmailId: 0,
      selectedFaxId: 0,
    },
  };
}

export function buildAlertRulePayload(rule, deviceId, activeStatusTypeId) {
  return {
    alert_type_id: numId(rule.alert_type_id),
    alert_sub_type_id: numId(rule.alert_sub_type_id) || null,
    event_type_id: numId(rule.event_type_id),
    send_to_type_id: numId(rule.send_to_type_id),
    address_id: numId(rule.contact_address_id),
    status_type_id: numId(activeStatusTypeId) || 1,
    device_id: String(deviceId),
    module_id: numId(rule.module_id),
    frequency: Number(rule.frequency) || 60,
    template_id: numId(rule.template_id),
  };
}

// Maps resolved tank geometry onto the asset's attribute IDs. Round tanks
// store their diameter in the width attribute (32); a horizontal cylinder's
// full height is also its diameter, so it is written to depth (33) too -
// these keys match the backend telemetry/volume calculations.
// clearUnused writes blank values for dimensions this shape does not use;
// callers that cannot persist blanks (the completion payload) must drop them.
export function tankAttributeMap(geometry, attributeDefs, clearUnused = false) {
  const byRole = {};
  (attributeDefs || []).forEach((def) => {
    if (def.role) byRole[def.role] = def;
  });
  const idFor = (role, fallbackId) => byRole[role]?.attribute_id ?? fallbackId;
  const values = {};
  const put = (role, fallbackId, value, clear = false) => {
    const id = idFor(role, fallbackId);
    if (id == null) return;
    if (value !== "" && value != null) values[String(id)] = String(value);
    else if (clear) values[String(id)] = "";
  };
  const dims = geometry.values;
  const round = geometry.shape === SHAPE.CYLINDER_VERTICAL ||
    geometry.shape === SHAPE.CYLINDER_HORIZONTAL;
  const horizontal = geometry.shape === SHAPE.CYLINDER_HORIZONTAL;
  const length = geometry.shape === SHAPE.CYLINDER_VERTICAL ? "" : dims.length_in;
  const width = round ? dims.diameter_in
    : geometry.shape === SHAPE.SQUARE ? dims.length_in : dims.width_in;
  const depth = horizontal ? dims.diameter_in : dims.depth_in;
  put("length", 31, length, clearUnused);
  put("width", 32, width, clearUnused);
  put("depth", 33, depth, clearUnused);
  put("shape", 34, shapeBackendValue(geometry.shape));
  put("capacity", 3, dims.capacity_gallons);
  put("usable_capacity", 39, dims.usable_capacity_gallons);
  put("max_fill", 45, dims.max_fill_pct);
  return values;
}

// Lowercase keys are what the backend volume/ullage maths compare against
// (e.g. "rectangle", "cylinder_vertical"); display labels stay human-readable
// in the UI instead of being written into the attribute value.
function shapeBackendValue(shape) {
  const map = {
    [SHAPE.RECTANGLE]: "Rectangle",
    [SHAPE.SQUARE]: "Square",
    [SHAPE.CYLINDER_VERTICAL]: "cylinder_vertical",
    [SHAPE.CYLINDER_HORIZONTAL]: "cylinder_horizontal",
  };
  return map[shape] || "";
}
